import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaTrash, FaPlus, FaExternalLinkAlt, FaEdit, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

const STORE_API_URL = process.env.REACT_APP_API_URL;

const DocumentManager = forwardRef(({ selectedDocuments = [], onDocumentsChange, disabled = false, crtId = null }, ref) => {
  const { token } = useContext(AuthContext);
  
  // Available documents from the library
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  
  // Simple upload state for drag-and-drop
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Search filter only
  const [searchFilter, setSearchFilter] = useState('');
  
  // Debounced search state
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editFormData, setEditFormData] = useState({
    display_name: '',
    type: '',
    description: ''
  });

  // Store fetched document details
  const [documentDetails, setDocumentDetails] = useState(new Map());
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Track current document IDs to avoid unnecessary refetches
  const currentDocumentIdsRef = useRef([]);

  // Local state for custom names (unsaved changes)
  const [localCustomNames, setLocalCustomNames] = useState(new Map());

  // Handle search input with debouncing
  const handleSearchChange = (value) => {
    setSearchFilter(value);
    
    // Show search in progress immediately for responsive feedback
    if (value.length >= 2) {
      setSearchInProgress(true);
    } else {
      setSearchInProgress(false);
    }
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
      setSearchInProgress(false);
    }, 500); // 500ms delay
  };

  // Effect for debounced search
  useEffect(() => {
    if (token) {
      // Only fetch documents if search term is at least 2 characters
      if (debouncedSearchTerm.trim().length >= 2) {
        fetchDocuments();
      } else {
        // Only clear documents if we're going below minimum length
        // This prevents flickering when switching between valid searches
        setAvailableDocuments([]);
        setLoading(false);
      }
    }
  }, [token, debouncedSearchTerm]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (token) {
      // Only fetch documents if there's a search term
      if (searchFilter.trim()) {
        fetchDocuments();
      } else {
        setAvailableDocuments([]); // Clear documents when no search
      }
    }
  }, [token, searchFilter]);

  // Fetch document details when document IDs change (not when custom names change)
  useEffect(() => {
    const documentIds = selectedDocuments.map(doc => doc.document_id).sort();
    
    // Only refetch if the document IDs have actually changed
    const idsChanged = documentIds.length !== currentDocumentIdsRef.current.length || 
                      documentIds.some((id, index) => id !== currentDocumentIdsRef.current[index]);
    
    if (idsChanged && documentIds.length > 0 && token) {
      currentDocumentIdsRef.current = documentIds;
      fetchDocumentDetails();
    } else if (documentIds.length === 0) {
      currentDocumentIdsRef.current = [];
      setDocumentDetails(new Map());
    }
  }, [selectedDocuments, token]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    triggerUpload: (files) => {
      handleDirectUpload(files);
    }
  }));

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
        params.append('require_search', 'true'); // Require search for autocomplete
      }

      const response = await fetch(`${STORE_API_URL}/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch documents');
      const docs = await response.json();
      setAvailableDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentDetails = async () => {
    setLoadingDetails(true);
    try {
      const documentIds = selectedDocuments.map(doc => doc.document_id).filter(Boolean);
      
      if (documentIds.length === 0) {
        setDocumentDetails(new Map());
        return;
      }

      const response = await fetch(`${STORE_API_URL}/documents/batch?ids=${documentIds.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch document details');
      const details = await response.json();
      
      // Create a map of document_id -> document details
      const detailsMap = new Map();
      details.forEach(doc => {
        detailsMap.set(doc._id, doc);
      });
      
      setDocumentDetails(detailsMap);
    } catch (error) {
      console.error('Error fetching document details:', error);
      toast.error('Failed to load document details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAddDocument = (document) => {
    // Check if document is already selected
    if (selectedDocuments.find(d => d.document_id === document._id)) {
      toast.warning('Document is already selected');
      return;
    }

    const newSelection = [
      ...selectedDocuments,
      {
        document_id: document._id,
        custom_name: '' // User can customize this name for this specific CRT
      }
    ];

    onDocumentsChange(newSelection);

    // Add this CRT to the document's usage list (if crtId is available)
    if (crtId) {
      addCRTToDocument(document._id, crtId);
    }
  };

  const handleRemoveDocument = (documentId) => {
    const documentToRemove = selectedDocuments.find(d => d.document_id === documentId);
    if (!documentToRemove) return;

    const newSelection = selectedDocuments.filter(d => d.document_id !== documentId);
    onDocumentsChange(newSelection);

    // Remove this CRT from the document's usage list (if crtId is available)
    if (crtId) {
      removeCRTFromDocument(documentId, crtId);
    }
  };

  const handleCustomNameChange = (documentId, customName) => {
    const newSelection = selectedDocuments.map(d => 
      d.document_id === documentId ? { ...d, custom_name: customName } : d
    );
    onDocumentsChange(newSelection);
  };

  // Handle input changes (local state only)
  const handleCustomNameInput = (documentId, value) => {
    setLocalCustomNames(prev => new Map(prev.set(documentId, value)));
  };

  // Handle manual save
  const handleSaveCustomName = (documentId) => {
    const newName = localCustomNames.get(documentId) || '';
    handleCustomNameChange(documentId, newName);
    // Remove from local state since it's now saved
    setLocalCustomNames(prev => {
      const newMap = new Map(prev);
      newMap.delete(documentId);
      return newMap;
    });
  };

  // Get current value (local or saved)
  const getCurrentCustomName = (doc) => {
    return localCustomNames.has(doc.document_id) 
      ? localCustomNames.get(doc.document_id) 
      : (doc.custom_name || '');
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = (doc) => {
    const localValue = localCustomNames.get(doc.document_id);
    return localValue !== undefined && localValue !== (doc.custom_name || '');
  };

  // Initialize local state when selectedDocuments change
  useEffect(() => {
    selectedDocuments.forEach(doc => {
      if (!localCustomNames.has(doc.document_id) && doc.custom_name) {
        setLocalCustomNames(prev => new Map(prev.set(doc.document_id, doc.custom_name)));
      }
    });
  }, [selectedDocuments]);

  const addCRTToDocument = async (documentId, crtId) => {
    try {
      await fetch(`${STORE_API_URL}/documents/${documentId}/add-crt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ crt_id: crtId })
      });
    } catch (error) {
      console.error('Error adding CRT to document:', error);
    }
  };

  const removeCRTFromDocument = async (documentId, crtId) => {
    try {
      await fetch(`${STORE_API_URL}/documents/${documentId}/remove-crt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ crt_id: crtId })
      });
    } catch (error) {
      console.error('Error removing CRT from document:', error);
    }
  };

  const handleDirectUpload = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('documents', file);
      });

      // Upload files to storage with duplicate detection
      const uploadResponse = await fetch(`${STORE_API_URL}/upload/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload files');
      const uploadResult = await uploadResponse.json();

      setUploadProgress(50);

      const newSelectedDocuments = [];

      // Handle uploaded files - create document records
      if (uploadResult.uploaded && uploadResult.uploaded.length > 0) {
        const documentPromises = uploadResult.uploaded.map(async (uploadedFile) => {
          const documentData = {
            display_name: uploadedFile.original_filename.replace(/\.[^/.]+$/, ""), // Remove extension
            original_filename: uploadedFile.original_filename,
            type: 'Service Manual', // Default type
            description: '', // Empty description
            url: uploadedFile.url,
            file_key: uploadedFile.key,
            file_size: uploadedFile.file_size,
            mime_type: uploadedFile.mime_type,
            file_hash: uploadedFile.file_hash // Include file hash
          };

          const response = await fetch(`${STORE_API_URL}/documents`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(documentData)
          });

          if (!response.ok) throw new Error('Failed to create document record');
          return response.json();
        });

        const createdDocs = await Promise.all(documentPromises);
        
        // Add uploaded documents to selection
        const uploadedSelections = createdDocs.map(docResponse => ({
          document_id: docResponse.document._id,
          custom_name: '' // User can customize this name for this specific CRT
        }));
        
        newSelectedDocuments.push(...uploadedSelections);

        // Add CRT to each uploaded document's usage list
        if (crtId) {
          for (const docResponse of createdDocs) {
            addCRTToDocument(docResponse.document._id, crtId);
          }
        }
      }

      // Handle duplicates - add existing documents to selection
      if (uploadResult.duplicates && uploadResult.duplicates.length > 0) {
        const duplicateSelections = uploadResult.duplicates.map(duplicate => ({
          document_id: duplicate.existing_document._id,
          custom_name: '' // User can customize this name for this specific CRT
        }));
        
        newSelectedDocuments.push(...duplicateSelections);

        // Add CRT to each duplicate document's usage list
        if (crtId) {
          for (const duplicate of uploadResult.duplicates) {
            addCRTToDocument(duplicate.existing_document._id, crtId);
          }
        }
      }

      setUploadProgress(100);

      // Add to existing selected documents (filter out any that are already selected)
      const existingDocIds = selectedDocuments.map(doc => doc.document_id);
      const uniqueNewSelections = newSelectedDocuments.filter(
        newDoc => !existingDocIds.includes(newDoc.document_id)
      );
      
      const updatedSelection = [...selectedDocuments, ...uniqueNewSelections];
      onDocumentsChange(updatedSelection);

      // Create success message
      let message = '';
      if (uploadResult.uploaded_count > 0) {
        message += `${uploadResult.uploaded_count} new document(s) uploaded`;
      }
      if (uploadResult.duplicate_count > 0) {
        if (message) message += ' and ';
        message += `${uploadResult.duplicate_count} duplicate(s) detected (using existing documents)`;
      }
      message += '! You can edit metadata from the Documents admin page.';

      toast.success(message);
      
      // Only refresh documents list if we're currently showing search results
      if (debouncedSearchTerm.trim().length >= 2) {
        fetchDocuments();
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleEditDocument = async (documentId) => {
    try {
      // Fetch full document details
      const response = await fetch(`${STORE_API_URL}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to fetch document details');
      const document = await response.json();
      
      setEditingDocument(document);
      setEditFormData({
        display_name: document.display_name || '',
        type: document.type || '',
        description: document.description || ''
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Failed to load document details');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingDocument) return;
    
    try {
      const response = await fetch(`${STORE_API_URL}/documents/${editingDocument._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      const updatedDoc = await response.json();

      if (!response.ok) {
        throw new Error(updatedDoc?.error || updatedDoc?.message || 'Failed to update document');
      }

      if (updatedDoc && updatedDoc.document) {
        toast.success('Document updated successfully');
              // Update the cached document details
      const updatedDetailsMap = new Map(documentDetails);
      updatedDetailsMap.set(editingDocument._id, updatedDoc.document);
      setDocumentDetails(updatedDetailsMap);
      } else {
        toast.success('Document updated, but no document data returned.');
      }
      
      // Close modal
      setShowEditModal(false);
      setEditingDocument(null);
      
      // Refresh available documents if showing search results
      if (debouncedSearchTerm.trim().length >= 2) {
        fetchDocuments();
      }
      
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error(error.message || 'Failed to update document');
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingDocument(null);
    setEditFormData({
      display_name: '',
      type: '',
      description: ''
    });
  };

  const getProgressColor = (progress) => {
    if (progress < 50) return '#f44336'; // Red
    if (progress < 75) return '#ff9800'; // Orange
    return '#4caf50'; // Green
  };

  return (
    <div className="document-manager" style={{ width: '100%' }}>
      <h6 className="mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}><a href="/superadmin/documents" style={{ textDecoration: 'none', color: 'inherit' }}>Documents & Manuals (Shared Library)</a></h6>
      
      {/* Selected Documents */}
      {selectedDocuments.length > 0 && (
        <div className="selected-documents mb-3" style={{ width: '100%' }}>
          <h7 className="text-muted">Selected Documents:</h7>
          {loadingDetails ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading document details...</span>
            </div>
          ) : (
            selectedDocuments.map((doc, index) => {
              const details = documentDetails.get(doc.document_id);
              if (!details) {
                return (
                  <div key={`${doc.document_id}-${index}`} className="selected-document d-flex align-items-center mb-2 p-2 border rounded" style={{ width: '100%' }}>
                    <div className="text-muted">Document details not found</div>
                  </div>
                );
              }
              return (
                <div key={`${doc.document_id}-${index}`} className="selected-document d-flex align-items-center mb-2 p-2 border rounded" style={{ width: '100%' }}>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                      <a href={details.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none me-2">
                        <FaExternalLinkAlt size={12} />
                      </a>
                      <strong 
                        className="me-2" 
                        style={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          maxWidth: '200px'
                        }}
                        title={doc.custom_name || details.display_name}
                      >
                        {doc.custom_name || details.display_name}
                      </strong>
                      <span className="badge bg-primary ms-2">{details.type}</span>
                    </div>
                    <small 
                      className="text-muted d-block" 
                      style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                      }}
                      title={`${details.original_filename}${details.file_size ? ` (${formatFileSize(details.file_size)})` : ''}`}
                    >
                      {details.original_filename} {details.file_size && `(${formatFileSize(details.file_size)})`}
                    </small>
                  </div>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Custom name"
                      value={getCurrentCustomName(doc)}
                      onChange={(e) => handleCustomNameInput(doc.document_id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveCustomName(doc.document_id);
                        }
                      }}
                      disabled={disabled}
                      style={{ 
                        width: '150px', 
                        marginRight: '8px', 
                        marginLeft: '8px', 
                        flex: '1',
                        borderColor: hasUnsavedChanges(doc) ? '#ffc107' : undefined
                      }}
                    />
                    {hasUnsavedChanges(doc) && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleSaveCustomName(doc.document_id)}
                        disabled={disabled}
                        className="me-2 d-flex align-items-center justify-content-center"
                        title="Save Custom Name"
                        style={{ height: '32px', width: '32px', padding: 0 }}
                      >
                        <FaCheck style={{ verticalAlign: 'middle' }} />
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleEditDocument(doc.document_id)}
                      disabled={disabled}
                      className="me-2 d-flex align-items-center justify-content-center"
                      title="Edit Document"
                      style={{ height: '32px', width: '32px', padding: 0 }}
                    >
                      <FaEdit style={{ verticalAlign: 'middle', position: 'relative', top: '1px' }} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveDocument(doc.document_id)}
                      disabled={disabled}
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: '32px', width: '32px', padding: 0 }}
                    >
                      <FaTrash style={{ verticalAlign: 'middle' }} />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Upload Section - Drag and Drop */}
      {uploadProgress > 0 && (
        <div className="progress mb-3">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ 
              width: `${uploadProgress}%`,
              background: getProgressColor(uploadProgress),
              transition: 'width 0.4s ease-in-out',
              color: '#fff',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-valuenow={uploadProgress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {uploadProgress}%
          </div>
        </div>
      )}

      <div className="form-group upload-section" style={{ width: '100%' }}>
        <div 
          className={`drag-drop-container`}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = 'application/pdf';
            input.onchange = (e) => {
              const files = Array.from(e.target.files).filter(file => 
                file.type === 'application/pdf'
              );
              if (files.length > 0) {
                handleDirectUpload(files);
              } else {
                toast.error('Please select PDF files only');
              }
            };
            input.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add('dragging');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove('dragging');
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add('dragging');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove('dragging');
            
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              // Process dropped files
              const files = Array.from(e.dataTransfer.files).filter(file => 
                file.type === 'application/pdf'
              );
              
              if (files.length === 0) {
                toast.error('Please drop PDF files only');
                return;
              }
              
              if (files.length !== e.dataTransfer.files.length) {
                toast.warning('Only PDF files were accepted');
              }
              
              // Upload directly
              handleDirectUpload(files);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="upload-icon">
            <path d="M12 17L12 3" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 7L12 3L17 7" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 21H4" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="drag-drop-text">Drop PDF files here</div>
          <div className="drag-drop-subtext">or click to upload and auto-add to this CRT</div>
          <div className="drag-drop-subtext">Edit metadata later in Documents admin if needed</div>
        </div>
      </div>

      {/* Controls */}
      <div className="document-controls mb-3" style={{ width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <Form.Control
            type="text"
            size="sm"
            placeholder="Search existing documents (minimum 2 characters)..."
            value={searchFilter}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{ width: '100%', paddingRight: searchInProgress || loading ? '40px' : '12px' }}
          />
          {(searchInProgress || loading) && (
            <div style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Spinner animation="border" size="sm" style={{ width: '16px', height: '16px' }} />
            </div>
          )}
        </div>
        {searchFilter.length > 0 && searchFilter.length < 2 && (
          <small className="text-muted d-block mt-1">Type at least 2 characters to search</small>
        )}
      </div>

      {/* Available Documents */}
      <div className="available-documents" style={{ width: '100%', maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
        {!debouncedSearchTerm.trim() || debouncedSearchTerm.trim().length < 2 ? (
          <Alert variant="light" className="mb-0 text-center">
            <div style={{ color: '#6c757d' }}>
              <strong>Type at least 2 characters to search for documents</strong>
              <br />
              <small>Or drag & drop PDF files to upload new ones</small>
            </div>
          </Alert>
        ) : availableDocuments.length === 0 && !loading && !searchInProgress ? (
          <Alert variant="info" className="mb-0">
            No documents found matching "{debouncedSearchTerm}". Try different keywords or upload new documents.
          </Alert>
        ) : (
          availableDocuments.map(doc => (
            <div key={doc._id} className="available-document d-flex align-items-center mb-2 p-2 border rounded">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                  <strong 
                    className="me-2" 
                    style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      maxWidth: '250px'
                    }}
                    title={doc.display_name}
                  >
                    {doc.display_name}
                  </strong>
                  <span className="badge bg-primary ms-2">{doc.type}</span>
                  {doc.usage_count > 0 && (
                    <span className="badge bg-success ms-1">Used by {doc.usage_count} CRT(s)</span>
                  )}
                </div>
                <div 
                  style={{ 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    maxWidth: '350px'
                  }}
                  title={`${doc.original_filename}${doc.file_size ? ` (${formatFileSize(doc.file_size)})` : ''}${doc.description ? ` - ${doc.description}` : ''}`}
                >
                  <small className="text-muted">
                    {doc.original_filename} {doc.file_size && `(${formatFileSize(doc.file_size)})`}
                    {doc.description && ` - ${doc.description}`}
                  </small>
                </div>
              </div>
              <div className="d-flex">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleAddDocument(doc)}
                  disabled={disabled || selectedDocuments.find(d => d.document_id === doc._id)}
                  className="me-2 d-flex align-items-center justify-content-center"
                  title="Add to this CRT"
                  style={{ height: '32px', width: '32px', padding: 0 }}
                >
                  <FaPlus style={{ verticalAlign: 'middle' }} />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => window.open(doc.url, '_blank')}
                  title="View Document"
                  className="d-flex align-items-center justify-content-center"
                  style={{ height: '32px', width: '32px', padding: 0 }}
                >
                  <FaExternalLinkAlt style={{ verticalAlign: 'middle' }} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Document Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingDocument && (
            <Form>
              <div className="d-flex mb-3">
                <div className="flex-grow-1 me-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Display Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editFormData.display_name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, display_name: e.target.value }))}
                      placeholder="Enter display name"
                    />
                  </Form.Group>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={editFormData.type}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="">Select Type</option>
                      <option value="Service Manual">Service Manual</option>
                      <option value="User Manual">User Manual</option>
                      <option value="Training Document">Training Document</option>
                      <option value="Technical Specs">Technical Specs</option>
                      <option value="Schematics">Schematics</option>
                      <option value="Datasheet">Datasheet</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description (optional)"
                />
              </Form.Group>

              <div className="mb-3">
                <small className="text-muted">
                  <strong>File:</strong> {editingDocument.original_filename} 
                  {editingDocument.file_size && ` (${formatFileSize(editingDocument.file_size)})`}
                </small>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveEdit}
            disabled={!editFormData.display_name.trim()}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
});

export default DocumentManager; 